import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Landing from "./pages/Landing";
import MealPlans from "./pages/MealPlans";
import Scan from "./pages/Scan";
import GuestAlertSync from "./components/GuestAlertSync";
import Recipes from "./pages/Recipes";
import Deals from "./pages/Deals";
import Budget from "./pages/Budget";
import Favorites from "./pages/Favorites";
import Auth from "./pages/Auth";
import ProfileOnboarding from "./pages/ProfileOnboarding";
import Subscription from "./pages/Subscription";
import AdminDashboard from "./pages/AdminDashboard";
import SettingsPage from "./pages/Settings";
import NotificationsPage from "./pages/Notifications";
import SupportPage from "./pages/Support";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ShareDeal from "./pages/ShareDeal";
import Earnings from "./pages/Earnings";
import ShareReceive from "./pages/ShareReceive";
import PartnershipDemo from "./pages/PartnershipDemo";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import GlobalAuthBanner from "./components/GlobalAuthBanner";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { ListProvider } from "./contexts/ListContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WeeklyAdsAccessProvider } from "./contexts/WeeklyAdsAccessContext";
import { GuestProvider } from "./contexts/GuestContext";

function PremiumRoute({ children }) {
  const { hasPremiumAccess } = useAuth();
  if (!hasPremiumAccess) return <Navigate to="/subscription" replace />;
  return children;
}

function AuthedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth?mode=signup" replace />;
  return children;
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-green-500 mb-2">Foodz Lizts</h1>
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Show profile onboarding for freshly signed-up users
  if (user && !user.profile_completed) {
    return <ProfileOnboarding />;
  }

  // Everyone (guest + signed-in) shares the same route tree — guest gating is
  // handled inside individual pages via the GuestContext + SignupPromptModal.
  return (
    <GuestProvider>
      <ListProvider>
        <WeeklyAdsAccessProvider>
          <GlobalAuthBanner />
          <GuestAlertSync />
          <Routes>
            {/* Fully public pages — no chrome */}
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/share/:dealId" element={<ShareDeal />} />
            <Route path="/share-receive" element={<ShareReceive />} />
            <Route path="/weekly-ads/partnership-demo" element={<PartnershipDemo />} />

            {/* App shell — public + gated */}
            <Route
              path="/*"
              element={
                <Layout>
                  <Routes>
                    {/* Public app-shell routes */}
                    <Route path="deals" element={<Deals />} />
                    <Route path="support" element={<SupportPage />} />

                    {/* Signed-in required (but not premium) */}
                    {/* Home = grocery list dashboard. Fully open to guests so
                        they can build a list without a signup wall. */}
                    <Route path="home" element={<Home />} />
                    <Route path="scan" element={<Scan />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="settings" element={<AuthedRoute><SettingsPage /></AuthedRoute>} />
                    <Route path="subscription" element={<AuthedRoute><Subscription /></AuthedRoute>} />
                    <Route path="subscription/success" element={<AuthedRoute><Subscription /></AuthedRoute>} />
                    <Route path="admin" element={<AuthedRoute><AdminDashboard /></AuthedRoute>} />
                    <Route path="earnings" element={<AuthedRoute><Earnings /></AuthedRoute>} />

                    {/* Premium-gated */}
                    <Route path="meal-plans" element={<AuthedRoute><PremiumRoute><MealPlans /></PremiumRoute></AuthedRoute>} />
                    <Route path="recipes" element={<AuthedRoute><PremiumRoute><Recipes /></PremiumRoute></AuthedRoute>} />
                    <Route path="budget" element={<Budget />} />
                    <Route path="favorites" element={<AuthedRoute><PremiumRoute><Favorites /></PremiumRoute></AuthedRoute>} />

                    {/* Legacy / dashboard alias */}
                    <Route path="dashboard" element={<Navigate to="/home" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </WeeklyAdsAccessProvider>
      </ListProvider>
    </GuestProvider>
  );
}

function App() {
  return (
    <div className="App">
      <PWAInstallPrompt />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
      <SonnerToaster position="top-center" richColors />
    </div>
  );
}

export default App;
